import axios, { AxiosResponse } from "axios";
import { business_integrations, IBranding, IMxmerchantPaymentCaptureFormAnswer } from "lobbie";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import CreditCardInput from "src/components/library/everyone/CreditCardInput";
import FullScreenLoadingSpinner from "src/components/shared/loading/FullScreenLoadingSpinner";
import SchedulingBrandingLogo from "src/components/shared/scheduling/cards/SchedulingBrandingLogo";
import { BASE_API_URL, BASE_PUBLIC_API_ROUTE } from "src/constants";
import { MXMERCHANT_BASE_URL_CHECKOUT } from "src/constants/mxmerchant";
import { notify } from "src/utils";

interface IInvoiceLineItemDisplay {
    name: string;
    amountInCents: number;
}

interface IPaymentData {
    firstName: string;
    lastName: string;
    homeAddress: string;
    homeAddress2?: string;
    invoiceLineItems: IInvoiceLineItemDisplay[];
    totalAmountInCents: number;
    surcharge: boolean;
    branding?: IBranding;
}

const PatientRemoteCareConversion = () => {
    const [actionId, setActionId] = useState<string | null>(null);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [paymentProcessed, setPaymentProcessed] = useState(false);
    const [paymentData, setPaymentData] = useState<IPaymentData | undefined>();
    const [cardDetails, setCardDetails] = useState<business_integrations.ICardDetails>({
        number: "",
        expiryMonth: "",
        expiryYear: "",
        cvv: "",
        cardholderName: "",
        billingAddress: "",
        billingAddressStreet: "",
        billingAddressPostalCode: "",
        billingAddressCity: "",
        billingAddressState: "",
        billingAddressCountry: "US",
    });

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const actionId = queryParams.get("actionId");
        if (actionId) {
            setActionId(actionId);
        }
    }, [location.search]);

    useEffect(() => {
        if (paymentData) {
            setCardDetails({
                ...cardDetails,
                cardholderName: `${paymentData?.firstName} ${paymentData?.lastName}`,
            });
        }
    }, [paymentData]);

    useEffect(() => {
        if (actionId) {
            axios
                .get(
                    `${BASE_API_URL}/${BASE_PUBLIC_API_ROUTE}/remote-care/patient-payment-info/${actionId}`,
                )
                .then((response) => {
                    if (response?.data) {
                        setPaymentData(response.data as IPaymentData);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching remote conversion data:", error);
                });
        }
    }, [actionId]);

    const saveAndProcessPayment = (ccFields: IMxmerchantPaymentCaptureFormAnswer) => {
        //Save payment method and process invoice
        axios
            .post(
                `${BASE_API_URL}/${BASE_PUBLIC_API_ROUTE}/remote-care/patient-payment-info/handle_payment/${actionId}`,
                ccFields,
            )
            .then((response) => {
                if (response.status === 204) {
                    // Handle success
                    console.log("Payment processed successfully:", response.data);
                    setPaymentProcessed(true);
                } else {
                    // Handle error
                    notify({
                        level: "error",
                        message: "Error processing payment",
                        title: "Payment Error",
                    });
                }
            })
            .catch((error) => {
                console.error("Error processing payment:", error);
                notify({
                    level: "error",
                    message: "Error processing payment",
                    title: "Payment Error",
                });
            })
            .finally(() => {
                setProcessingPayment(false);
            });
    };

    const handlePayment = useCallback(() => {
        //Validate card details
        if (cardDetails) {
            setProcessingPayment(true);
            //Fetch Token
            axios
                .get(
                    `${BASE_API_URL}/${BASE_PUBLIC_API_ROUTE}/remote-care/patient-payment-info/token/${actionId}`,
                )
                .then((response) => {
                    const initData = response.data as {
                        clientSideSecret: string;
                        mxMerchantCustomerId: string;
                    };

                    if (!initData || !initData.clientSideSecret || !initData.mxMerchantCustomerId) {
                        notify({
                            level: "error",
                            message: "Error fetching payment data",
                            title: "Payment Error",
                        });
                        setProcessingPayment(false);
                        return;
                    }

                    axios
                        .post<
                            business_integrations.IMxMerchantVaultCardResponse,
                            AxiosResponse<business_integrations.IMxMerchantVaultCardResponse>,
                            business_integrations.IMxMerchantVaultCardRequest
                        >(
                            `${MXMERCHANT_BASE_URL_CHECKOUT}/vault?token=${initData.clientSideSecret}&customerid=${initData.mxMerchantCustomerId}`,
                            {
                                ...cardDetails,
                                name: cardDetails.cardholderName,
                                number: cardDetails.number.replace(/\s/g, ""),
                                cvv: parseInt(cardDetails.cvv, 10),
                                avsStreet: cardDetails.billingAddressStreet,
                                avsZip: cardDetails.billingAddressPostalCode,
                                expiryMonth: cardDetails.expiryMonth,
                                expiryYear: cardDetails.expiryYear,
                            },
                        )
                        .then((response) => {
                            if (response.status === 200) {
                                if (response.data) {
                                    const ccFields: IMxmerchantPaymentCaptureFormAnswer = {
                                        cardHolderName: cardDetails.cardholderName,
                                        cardToken: response.data as string,
                                        isAuthorized: true,
                                        authorizationTimestamp: new Date().toISOString(),
                                        last4: cardDetails.number.slice(-4),
                                        brand: "PLACEHOLDER",
                                        expiryMonth: cardDetails.expiryMonth,
                                        expiryYear: cardDetails.expiryYear,
                                        billingAddress: cardDetails.billingAddress,
                                    };
                                    saveAndProcessPayment(ccFields);
                                }
                            }
                        })
                        .catch((error) => {
                            console.error("Error processing payment:", error);
                            notify({
                                level: "error",
                                message: "Error processing payment",
                                title: "Payment Error",
                            });
                            setProcessingPayment(false);
                        });
                })
                .catch((error) => {
                    console.error("Error processing payment:", error);
                });
        }
    }, [cardDetails, actionId]);

    const surchargeAmount = useMemo(() => {
        if (paymentData) {
            return paymentData.totalAmountInCents * 0.035;
        }
        return 0;
    }, [paymentData]);
    if (!paymentData) {
        return <FullScreenLoadingSpinner />;
    }
    if (paymentProcessed) {
        return (
            <div className="container">
                <div className="row mt-4">
                    {paymentData?.branding && (
                        <>
                            <div className="col-4">&nbsp;</div>
                            <div className="col-4 text-center">
                                <SchedulingBrandingLogo branding={paymentData?.branding} />
                            </div>
                            <div className="col-4">&nbsp;</div>
                        </>
                    )}
                </div>
                <div className="row mt-4">
                    <div className="col text-center">
                        <h4>Payment Processed Successfully</h4>
                        <p>Thank you for your payment!</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <Modal show={processingPayment}>
                <Modal.Header>
                    <Modal.Title>Processing Payment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Please wait while we process your payment...</p>
                </Modal.Body>
            </Modal>
            <div className="row mt-4">
                {paymentData?.branding && (
                    <>
                        <div className="col-3">
                            <SchedulingBrandingLogo branding={paymentData?.branding} />
                        </div>
                        <div className="col-9">&nbsp;</div>
                    </>
                )}
            </div>
            <div className="row mt-4">
                <div className="col-md-6">
                    <h4>Invoice Details</h4>
                    <hr />
                    <table className="table table-bordered" style={{ maxWidth: "600px" }}>
                        <tbody>
                            {paymentData.invoiceLineItems.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.name}</td>
                                    <td>${(item.amountInCents / 100).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td className="bold">
                                    {paymentData.surcharge ? "Subtotal" : "Total"}
                                </td>
                                <td className="bold">
                                    ${(paymentData.totalAmountInCents / 100).toFixed(2)}
                                </td>
                            </tr>
                            {paymentData.surcharge && (
                                <tr>
                                    <td className="bold">Total (3.5% Surcharge)</td>
                                    <td className="bold">
                                        $
                                        {(
                                            (paymentData.totalAmountInCents + surchargeAmount) /
                                            100
                                        ).toFixed(2)}
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </div>
                <div className="col-md-6">
                    <h4>Payment Information</h4>
                    <hr />
                    <CreditCardInput onCardDetailsChange={setCardDetails} />
                    <div className="row mt-3">
                        <div className="col text-right">
                            <Button onClick={handlePayment}>Make Payment</Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default PatientRemoteCareConversion;
